import { Box, Flex, Heading, Stack, Text, Textarea } from "@chakra-ui/react";
import { memo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { MinimumRequirementResponseType } from "../../../../autogen/bff-api";
import { StatusAccentBar } from "../../../../common/components/StatusAccentBar";
import { StatusDotLabel } from "../../../../common/components/StatusDotLabel";
import { verdictToStatusVariant } from "../../../../common/components/StatusVariant";
import { TipTap } from "../../../../common/input/TipTap/TipTap";
import { QualificationVerdictControl } from "./QualificationVerdictControl";

interface Props {
  id: string;
  title: string;
  description?: string | null;
  responseType: MinimumRequirementResponseType;
  supplierAnswer: boolean | undefined;
  supplierComment?: string | null;
  verdict: boolean | null;
  comment: string;
  onVerdictChange: (id: string, value: boolean | null) => void;
  onCommentChange: (id: string, value: string) => void;
  isReadOnly?: boolean;
}

// Memoized so editing one card does not re-render (and re-render the read-only TipTap description
// of) every other card. The parent passes stable per-id handlers, so only props of the edited row
// change.
export const MinimumRequirementReviewCard = memo(function MinimumRequirementReviewCard({
  id,
  title,
  description,
  responseType,
  supplierAnswer,
  supplierComment,
  verdict,
  comment,
  onVerdictChange,
  onCommentChange,
  isReadOnly,
}: Props) {
  const { t } = useTranslation();
  const followsSupplierAnswer = responseType === MinimumRequirementResponseType.YesNo;
  const effectiveVerdict = followsSupplierAnswer ? supplierAnswer ?? null : verdict;
  // A requirement the supplier never answered (e.g. added after this bid was submitted) cannot be
  // reviewed — the backend rejects a verdict for it — so lock the controls instead of letting the
  // buyer make an edit that would be discarded.
  const reviewable = supplierAnswer !== undefined;
  const locked = followsSupplierAnswer || !reviewable;

  return (
    <Box
      bg="smBackground"
      border="1px solid"
      borderColor="smBorder"
      rounded="xl"
      px={{ base: "4", md: "6" }}
      py="5"
      position="relative"
      overflow="hidden"
    >
      <StatusAccentBar variant={verdictToStatusVariant(effectiveVerdict)} />
      <Flex align="flex-start" justify="space-between" gap="4" flexWrap="wrap" mb="4">
        <Stack spacing="1" minW="0">
          <Heading as="h3" size="sm" color="smText">
            {title}
          </Heading>
          {description && (
            <Box fontSize="sm" color="smMuted">
              <TipTap content={description} editable={false} hasBorder={false} padding="0" maxHeight="" />
            </Box>
          )}
        </Stack>
        {locked ? (
          <QualificationVerdictControl value={effectiveVerdict} isLocked />
        ) : (
          <QualificationVerdictControl
            value={verdict}
            onChange={(v) => onVerdictChange(id, v)}
            isReadOnly={isReadOnly}
          />
        )}
      </Flex>

      <Stack spacing="4">
        <Stack spacing="2">
          <FieldLabel>{t("Supplier's answer")}</FieldLabel>
          {supplierAnswer === undefined ? (
            <Box
              border="1px dashed"
              borderColor="smBorder"
              rounded="lg"
              px="3"
              py="2.5"
              fontSize="sm"
              fontStyle="italic"
              color="smMuted"
            >
              {t("Not answered by supplier")}
            </Box>
          ) : (
            <Box bg="smBackgroundSecondary" rounded="lg" px="4" py="3.5">
              <Stack spacing="3" align="flex-start">
                <StatusDotLabel
                  variant={supplierAnswer ? "ok" : "issue"}
                  label={String(supplierAnswer ? t("Yes") : t("No"))}
                />
                {supplierComment && (
                  <Text color="smText" lineHeight="tall" whiteSpace="pre-wrap" maxW="82ch">
                    {supplierComment}
                  </Text>
                )}
              </Stack>
            </Box>
          )}
        </Stack>

        <Stack spacing="2">
          <FieldLabel>{t("Evaluation comment")}</FieldLabel>
          <Textarea
            value={comment}
            onChange={(e) => onCommentChange(id, e.target.value)}
            placeholder={String(t("Write comment..."))}
            rows={3}
            isReadOnly={isReadOnly || !reviewable}
            resize="vertical"
            bg="smBackground"
          />
        </Stack>
      </Stack>
    </Box>
  );
});

const FieldLabel = ({ children }: { children: ReactNode }) => (
  <Text fontSize="xs" fontWeight="semibold" color="smMuted" textTransform="uppercase" letterSpacing="wide">
    {children}
  </Text>
);
